import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DEMO_RECENT_LOCATIONS_PREFIX,
  DRIVER_ID,
  RIDER_ID,
  type DemoRole,
} from '@/lib/demo/constants';
import { clearDemoSession, getDemoRole, setDemoRole } from '@/lib/demo/session';
import { forgetStore } from '@/lib/demo/store';
import type { Counterpart } from '@/lib/demo/counterpart/scheduler';
import { DemoContext, type DemoContextValue, type DemoPerson } from './demoContext';

const PEOPLE: Record<DemoRole, DemoPerson> = {
  rider: { id: RIDER_ID, name: 'Maya Chen', email: 'maya@demo.parleyride' },
  driver: { id: DRIVER_ID, name: 'Andre Wallace', email: 'andre@demo.parleyride' },
};

export function DemoProvider({ children }: { children: React.ReactNode }) {
  // Read once at mount, so a reload resumes the demo the visitor was in.
  const [role, setRole] = useState<DemoRole | null>(() => getDemoRole());
  const [resetNonce, setResetNonce] = useState(0);

  const enterDemo = useCallback((next: DemoRole) => {
    setDemoRole(next);
    setRole(next);
  }, []);

  /**
   * Deliberately keeps the store: one dataset, two viewpoints.
   *
   * Workforce's boundary key is the vertical, and switching it REBUILDS the
   * store. Copying that here would throw away the ride the visitor just
   * booked, which is the one unforgivable bug this demo can have.
   */
  const switchRole = useCallback((next: DemoRole) => {
    setDemoRole(next);
    setRole(next);
  }, []);

  const exitDemo = useCallback(() => {
    clearDemoSession();
    forgetStore();
    setRole(null);
  }, []);

  const resetDemo = useCallback(() => {
    forgetStore();
    // useMapboxToken caches to localStorage, which outlives the demo session,
    // so it belongs in a reset too.
    try {
      localStorage.removeItem('mapbox_token');
    } catch {
      /* storage unavailable; nothing to clean up */
    }
    // The demo's own recent locations live outside the store (the shared hook
    // owns their key), so forgetStore does not reach them.
    try {
      const s = globalThis.sessionStorage;
      if (s) {
        for (const key of Object.keys(s)) {
          if (key.startsWith(DEMO_RECENT_LOCATIONS_PREFIX)) s.removeItem(key);
        }
      }
    } catch {
      /* storage unavailable; nothing to clean up */
    }
    setResetNonce((n) => n + 1);
  }, []);

  // The simulated counterpart on the other side of the negotiation. Started
  // per session and torn down on exit, reset or role change — a second one
  // running against the same store would bid against itself.
  const counterpart = useRef<Counterpart | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!role) return;

    void import('@/lib/demo/counterpart/scheduler')
      .then(({ startCounterpart }) => {
        if (cancelled) return;
        counterpart.current = startCounterpart(role);
      })
      // Without this the demo's headline feature can fail to load and say
      // nothing at all: the visitor sits on "Finding you a driver" forever and
      // the console is clean. A chunk that will not load is worth a line.
      .catch((err) => {
        console.error('Demo counterpart failed to start; nobody will respond:', err);
      });

    return () => {
      cancelled = true;
      counterpart.current?.stop();
      counterpart.current = null;
    };
  }, [role, resetNonce]);

  const value = useMemo<DemoContextValue>(
    () => ({
      isDemoMode: role !== null,
      role,
      person: role ? PEOPLE[role] : null,
      resetNonce,
      enterDemo,
      switchRole,
      exitDemo,
      resetDemo,
    }),
    [role, resetNonce, enterDemo, switchRole, exitDemo, resetDemo],
  );

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
}
